/**
 * NewsDetailPage — dedicated, fully-styled page for a single news article.
 * Replaces the old in-list modal so each article has its own URL, full layout
 * and is shareable. Route: /:lang/noticias/:id (and /:lang/news/:id).
 */
import React, { useEffect, useState } from 'react';
import { parseLocalDate } from '../lib/dateFmt';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, Loader2, AlertCircle, Newspaper } from 'lucide-react';
import { api, absoluteUrl } from '../lib/api';
import { normalizeHtml } from '../lib/htmlText';
import RichContentBlocks from '../components/RichContentBlocks';
import { useTranslation } from '../i18nContext';

const categoryColors = {
  actividad: 'bg-teal-50 text-teal-700',
  fit: 'bg-cmic-50 text-cmic-700',
  comunicado: 'bg-amber-50 text-amber-700',
  evento: 'bg-emerald-50 text-emerald-700',
};

export default function NewsDetailPage() {
  const { id } = useParams();
  const t = useTranslation();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/news/${id}`)
      .then(({ data }) => { if (alive) setItem(data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || (t.locale === 'en' ? 'News not found' : 'Noticia no encontrada')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id, t.locale]);

  const newsBase = `/${t.locale}/${t.locale === 'en' ? 'news' : 'noticias'}`;
  const dateLocale = t.locale === 'en' ? 'en-US' : 'es-MX';
  const formatDate = (s) => s ? parseLocalDate(s).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="news-detail-loading">
        <Loader2 size={28} className="animate-spin text-cmic-500" />
      </div>
    );
  }
  if (error || !item) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8" data-testid="news-detail-error">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-charcoal-800 mb-2">{t.locale === 'en' ? 'News not found' : 'Noticia no encontrada'}</h1>
          <p className="text-sm text-charcoal-500 mb-6">{error}</p>
          <button onClick={() => navigate(newsBase)} className="inline-flex items-center gap-2 text-cmic-600 hover:text-cmic-700 font-semibold">
            <ArrowLeft size={16} /> {t.locale === 'en' ? 'Back to news' : 'Volver a noticias'}
          </button>
        </div>
      </div>
    );
  }

  const category = t.news?.categories?.[item.category] || item.category;

  return (
    <div data-testid="news-detail-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 break-words pt-28 pb-10">
        <Link to={newsBase} data-testid="news-detail-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-charcoal-500 hover:text-cmic-600 mb-6">
          <ArrowLeft size={15} /> {t.locale === 'en' ? 'Back to news' : 'Volver a noticias'}
        </Link>

        {/* Featured image — shown at its natural aspect ratio, centered and
            height-capped, so portrait flyers and landscape banners both look
            good (no awkward wide-banner cropping). */}
        {item.image_url && (
          <figure className="mb-8 rounded-2xl overflow-hidden bg-charcoal-50 border border-charcoal-100 flex items-center justify-center" data-testid="news-detail-cover">
            <img
              src={absoluteUrl(item.image_url)}
              alt={item.title}
              className="max-h-[78vh] w-auto max-w-full object-contain"
            />
          </figure>
        )}

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${categoryColors[item.category] || 'bg-gray-100 text-gray-600'}`}>
            <Tag size={12} /> {category}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-charcoal-400">
            <Calendar size={12} /> {formatDate(item.published_at)}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-charcoal-900 leading-tight break-words">{item.title}</h1>

        {item.summary && (
          <p lang="es" className="mt-5 text-base sm:text-lg text-charcoal-600 leading-relaxed italic border-l-4 border-cmic-300 pl-4 break-words [hyphens:auto]">
            {item.summary}
          </p>
        )}

        {item.content && (
          <div
            lang="es"
            className="prose prose-lg prose-charcoal max-w-none mt-8 prose-headings:text-charcoal-800 prose-a:text-cmic-600 prose-strong:text-charcoal-800 prose-img:rounded-xl prose-img:max-w-full break-words [hyphens:auto]"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(item.content) }}
          />
        )}

        <RichContentBlocks blocks={item.content_blocks} />

        <div className="mt-12 pt-6 border-t border-gray-100">
          <Link to={newsBase} className="inline-flex items-center gap-2 text-cmic-600 hover:text-cmic-700 font-semibold">
            <Newspaper size={16} /> {t.locale === 'en' ? 'See all news' : 'Ver todas las noticias'}
          </Link>
        </div>
      </article>
    </div>
  );
}
